import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";
import { getPurchaseRequestUrl } from "../../../../utils/urls";

const ViewPurchaseRequest = () => {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const [purchaseRequest, setPurchaseRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("FACILITY:", selectedFacilityId, "REQ ID:", requestId);
    if (selectedFacilityId && requestId) {
      fetchPurchaseRequestDetails();
    }
  }, [selectedFacilityId, requestId]);
  

  const fetchPurchaseRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getPurchaseRequestUrl}/${selectedFacilityId}/${requestId}`,
        'GET'
      );

      if (response.success && response.data) {
        setPurchaseRequest(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch purchase request details', 'error');
        console.error('Error fetching purchase request details:', response);
      }
    } catch (error) {
      toastify('Error fetching purchase request details', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date in a readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format approval status with appropriate badge colors
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-success';
      case 'pending':
      case 'in_progress':
        return 'bg-warning';
      case 'rejected':
      case 'not approved':
        return 'bg-danger';
      case 'cancelled':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  // If no facility is selected, show a prompt
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view purchase request details.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/purchase_requests">
                    Purchase Requests
                  </Link>
                </li>
                <li className="breadcrumb-item">View Purchase Request</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <Link to="/facility/procurement_management/purchase_requests">
                  <i className="ti ti-arrow-narrow-left"></i> Back
                </Link>
              </div>
              <div>
                {purchaseRequest?.status?.toLowerCase() === 'pending' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/facility/procurement_management/purchase_requests/edit/${requestId}`)}
                  >
                    Edit Request
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          ) : purchaseRequest ? (
            <div className="card">
              <div className="card-header">
                <h5>Purchase Request Details</h5>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Request Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">IRF Number:</td>
                              <td>{purchaseRequest.irfNumber || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Request Date:</td>
                              <td>{formatDate(purchaseRequest.date)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Requested By:</td>
                              <td>{purchaseRequest.from || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Department:</td>
                              <td>{purchaseRequest.department || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Status:</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(purchaseRequest.status)}`}>
                                  {purchaseRequest.status ? purchaseRequest.status.charAt(0).toUpperCase() + purchaseRequest.status.slice(1) : 'N/A'}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">PO Status:</td>
                              <td>
                                <span className={`badge ${purchaseRequest.poStatus === 'PO Raised' ? 'bg-success' : 'bg-warning'}`}>
                                  {purchaseRequest.poStatus || 'Pending'}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Approval Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Approval Status:</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(purchaseRequest.approvalStatus)}`}>
                                  {purchaseRequest.approvalStatus ? 
                                    purchaseRequest.approvalStatus.replace('_', ' ').charAt(0).toUpperCase() + 
                                    purchaseRequest.approvalStatus.replace('_', ' ').slice(1) : 
                                    'N/A'}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Created At:</td>
                              <td>{formatDate(purchaseRequest.createdAt)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Last Updated:</td>
                              <td>{formatDate(purchaseRequest.updatedAt)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div className="row mb-4">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Requested Items</h6>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Item Description</th>
                                <th>Quantity</th>
                                <th>Unit of Measure</th>
                                <th>Remarks/Specification</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseRequest.items && purchaseRequest.items.length > 0 ? (
                                purchaseRequest.items.map((item, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.itemDescription || 'N/A'}</td>
                                    <td>{item.quantity || 'N/A'}</td>
                                    <td>{item.unitOfMeasure || 'N/A'}</td>
                                    <td>{item.remarksSpecification || 'N/A'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center">No items found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approvals Section */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Approval Workflow</h6>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Step</th>
                                <th>Name</th>
                                <th>Approvers</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Comments</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseRequest.approvals && purchaseRequest.approvals.length > 0 ? (
                                purchaseRequest.approvals.map((step, index) => (
                                  <React.Fragment key={index}>
                                    {step.approvers && step.approvers.map((approver, approverIndex) => (
                                      <tr key={`${index}-${approverIndex}`}>
                                        {approverIndex === 0 && (
                                          <>
                                            <td rowSpan={step.approvers.length}>{step.stepNumber}</td>
                                            <td rowSpan={step.approvers.length}>{step.stepName}</td>
                                          </>
                                        )}
                                        <td>
                                          {approver.userId ? (
                                            typeof approver.userId === 'object' ? 
                                              approver.userId.fullName || 'User' : 
                                              'User'
                                          ) : 'N/A'}
                                        </td>
                                        <td>
                                          <span className={`badge ${getStatusBadgeClass(approver.status)}`}>
                                            {approver.status ? 
                                              approver.status.charAt(0).toUpperCase() + approver.status.slice(1) : 
                                              'Pending'}
                                          </span>
                                        </td>
                                        <td>{formatDate(approver.actionDate)}</td>
                                        <td>{approver.comments || 'No comments'}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center">No approval workflow found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center">
                <p>Purchase request not found or unable to fetch details.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewPurchaseRequest;